import { isUnityTeardownActive } from "@/lib/unity/teardown-guard";
import type { BeforeSendFn, CaptureResult } from "posthog-js";

/**
 * Browser-extension / injected-script noise that floods Error Tracking.
 *
 * Shape observed in production (PLA-701):
 *   UnhandledRejection —
 *   "Non-Error promise rejection captured with value: Object Not Found Matching Id:N, MethodName:update, ParamCount:4"
 *
 * Samples have empty stacks, no in-app frames, and are not PlayMath host/Unity bugs.
 * See https://linear.app/playmath/issue/PLA-701
 */
export const OBJECT_NOT_FOUND_MATCHING_ID_PATTERN =
  /Object Not Found Matching Id:\d+,\s*MethodName:\w+,\s*ParamCount:\d+/i;

/**
 * iOS webview / in-app browser bridge noise (PLA-702).
 *
 * Shape observed in production:
 *   TypeError — undefined is not an object (evaluating 'window.webkit.messageHandlers')
 *   plus siblings: messageHandlers[e].postMessage, navigationPerformanceLoggerWithReply.postMessage
 *
 * Samples are Mobile Safari / iOS, Instagram in-app browser (sendPageHideMessage →
 * sendDataToNative), attributed to playmath.org because the injected script has no map.
 * See https://linear.app/playmath/issue/PLA-702
 */
// Tight match to the observed $exception value shape:
//   "undefined is not an object (evaluating 'window.webkit.messageHandlers…')"
export const WEBKIT_MESSAGE_HANDLERS_PATTERN = /evaluating 'window\.webkit\.messageHandlers/i;

/**
 * Unity WebGL teardown after overlay close / Quit() (grade-page investigation).
 *
 * Observed on listing URLs (`/pages/1st-grade`, `/games`, `/`) a few seconds
 * after `unity_game_load_aborted` + `router.back()`. Stacks point at
 * `_emscripten_webgl_destroy_context` / `GL.contexts[…].GLctx` or Unity's
 * eventHandler after the canvas is gone. Opaque `Script error.` is the same
 * burst when the browser strips the cross-origin framework frame.
 *
 * Only dropped while a Quit() / aborted-boot-then-Quit is in flight so
 * in-game crashes on `/games/[slug]` still reach Error Tracking. The host
 * parks the canvas until Quit settles; this filter is the backstop for
 * leftover framework throws, not a wall-clock mute.
 */
export const UNITY_TEARDOWN_GLCTX_PATTERN = /GLctx|GL\.contexts/i;
export const UNITY_TEARDOWN_EVENT_HANDLER_PATTERN =
  /eventHandler\.target|reading 'addEventListener'|evaluating 'eventHandler\.target/i;
export const UNITY_TEARDOWN_SCRIPT_ERROR_PATTERN = /^Script error\.?$/i;

type ExceptionListItem = {
  value?: unknown;
};

function exceptionMessageCandidates(properties: CaptureResult["properties"]): string[] {
  const candidates: string[] = [];

  const values = properties.$exception_values;
  if (Array.isArray(values)) {
    for (const value of values) {
      if (typeof value === "string") candidates.push(value);
    }
  } else if (typeof values === "string") {
    candidates.push(values);
  }

  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const item of list as ExceptionListItem[]) {
      if (typeof item?.value === "string") candidates.push(item.value);
    }
  }

  return candidates;
}

/** True when the exception message matches known browser-extension noise. */
export function isObjectNotFoundMatchingIdNoise(message: string): boolean {
  return OBJECT_NOT_FOUND_MATCHING_ID_PATTERN.test(message);
}

/** True when the exception message matches iOS webview messageHandlers noise. */
export function isWebkitMessageHandlersNoise(message: string): boolean {
  return WEBKIT_MESSAGE_HANDLERS_PATTERN.test(message);
}

/** True when the message is a Unity WebGL teardown throw (GLctx / eventHandler / Script error.). */
export function isUnityTeardownExceptionNoise(message: string): boolean {
  return (
    UNITY_TEARDOWN_GLCTX_PATTERN.test(message) ||
    UNITY_TEARDOWN_EVENT_HANDLER_PATTERN.test(message) ||
    UNITY_TEARDOWN_SCRIPT_ERROR_PATTERN.test(message)
  );
}

function isAlwaysDroppedExceptionNoise(message: string): boolean {
  return isObjectNotFoundMatchingIdNoise(message) || isWebkitMessageHandlersNoise(message);
}

function isKnownExceptionNoise(message: string): boolean {
  if (isAlwaysDroppedExceptionNoise(message)) return true;
  return isUnityTeardownActive() && isUnityTeardownExceptionNoise(message);
}

/** True when a CaptureResult is an $exception that should not be sent. */
export function shouldDropExceptionEvent(event: CaptureResult | null): boolean {
  if (!event || event.event !== "$exception") return false;
  return exceptionMessageCandidates(event.properties).some(isKnownExceptionNoise);
}

function shouldDropObjectNotFoundMatchingIdExceptionEvent(event: CaptureResult | null): boolean {
  if (!event || event.event !== "$exception") return false;
  return exceptionMessageCandidates(event.properties).some(isObjectNotFoundMatchingIdNoise);
}

function shouldDropWebkitMessageHandlersExceptionEvent(event: CaptureResult | null): boolean {
  if (!event || event.event !== "$exception") return false;
  return exceptionMessageCandidates(event.properties).some(isWebkitMessageHandlersNoise);
}

/**
 * Drop known non-actionable $exception noise before send (PLA-701 + PLA-702
 * plus leftover Unity teardown throws while Quit() is in flight). Prefer this over
 * remote-only suppression rules: remote rules can miss early page-load
 * exceptions before config loads (PostHog docs / GH #2327).
 */
export const dropKnownExceptionNoise: BeforeSendFn = (event) => {
  if (shouldDropExceptionEvent(event)) return null;
  return event;
};

/** PLA-701-only drop (Object Not Found Matching Id… UnhandledRejection). */
export const dropObjectNotFoundMatchingIdExceptions: BeforeSendFn = (event) => {
  if (shouldDropObjectNotFoundMatchingIdExceptionEvent(event)) return null;
  return event;
};

/** PLA-702-only drop (iOS webview / in-app browser bridge `webkit.messageHandlers`). */
export const dropWebkitMessageHandlersExceptions: BeforeSendFn = (event) => {
  if (shouldDropWebkitMessageHandlersExceptionEvent(event)) return null;
  return event;
};
